import { getCalendarWeekdayIndex, type CalendarWeekStart } from 'lib/datetime/calendarUtils'
import { startOfDay } from 'lib/datetime/dateUtils'

/** Month grids are Monday-first weeks: one column per day, at most 6 week rows */
export const DAYS_IN_WEEK = 7
export const MAX_MONTH_WEEK_ROWS = 6

interface RangeGlowSegment {
  /** 0-based week row within the month grid */
  row: number
  /** 0-based column (Monday-first), inclusive */
  startCol: number
  /** 0-based column (Monday-first), inclusive */
  endCol: number
  /** Segment contains the true range start (rounded start corners) */
  capStart: boolean
  /** Segment contains the true range end (rounded end corners) */
  capEnd: boolean
}

/**
 * Computes the per-week-row segments needed to draw a highlight around the whole
 * [rangeStart, rangeEnd] date range within a single calendar month grid.
 *
 * The range is clamped to the rendered month; edges created by clamping (month
 * boundaries) or week wraps are square (`capStart`/`capEnd` false), so a range
 * spanning multiple months reads as one continuous band across adjacent grids.
 */
export function getRangeGlowSegments(
  monthStart: Date,
  rangeStart: Date,
  rangeEnd: Date,
  weekStart: CalendarWeekStart = 1,
): Array<RangeGlowSegment> {
  const firstOfMonth = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1)
  const lastOfMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0)

  const start = startOfDay(rangeStart)
  const end = startOfDay(rangeEnd)
  if (end.getTime() < start.getTime()) return []

  const clampedStart = start.getTime() < firstOfMonth.getTime() ? firstOfMonth : start
  const clampedEnd = end.getTime() > lastOfMonth.getTime() ? lastOfMonth : end
  if (clampedStart.getTime() > clampedEnd.getTime()) return []

  const firstWeekdayIndex = getCalendarWeekdayIndex(firstOfMonth, weekStart)
  const slotOf = (date: Date) => firstWeekdayIndex + date.getDate() - 1
  const firstRow = Math.floor(slotOf(clampedStart) / DAYS_IN_WEEK)
  const lastRow = Math.floor(slotOf(clampedEnd) / DAYS_IN_WEEK)

  const segments: Array<RangeGlowSegment> = []
  for (let row = firstRow; row <= lastRow; row += 1) {
    segments.push({
      row,
      startCol: row === firstRow ? slotOf(clampedStart) % DAYS_IN_WEEK : 0,
      endCol: row === lastRow ? slotOf(clampedEnd) % DAYS_IN_WEEK : DAYS_IN_WEEK - 1,
      capStart: row === firstRow && clampedStart.getTime() === start.getTime(),
      capEnd: row === lastRow && clampedEnd.getTime() === end.getTime(),
    })
  }
  return segments
}
